import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { supplierFinanceApi, type SupplierFinanceOverview } from "../../api/supplierFinance";
import type { ApiError } from "../../api/client";
import { KPI } from "../../components/primitives.jsx";
import { FinanceFilters, type FiltersValue } from "../finance/FinanceFilters";
import { presetRange } from "../finance/usePeriod";
import { money, fmtDate } from "../finance/money";
import { AmountByCurrency } from "../../components/AmountByCurrency";
import { AgingChart, AgingStrip } from "../finance/AgingChart";
import type { Bucket } from "../../api/finance";
import { Pager } from "../../components/Pager";
import { TableSkeleton } from "../../components/Loading";
import { usePage } from "../../lib/usePage";
import { intlLocale } from "../../lib/format";
import { countryName } from "../../i18n/countries";

/**
 * Internal roll-up across in-scope supplier companies, the counterpart of finance/FinanceOverviewPage.
 *
 * <p>The one filter this screen does not have is the market, and that is the rule rather than a gap:
 * OTKUP has no market or country scope, unlike SALES (see {@code OtkupAccessChecker}). The period and
 * the search are the same as on the customer side, and {@code FinanceFilters} hides the market picker
 * by itself when the list is empty.
 *
 * <p>The list is paged. The server has always returned {@code page}, {@code size} and
 * {@code totalElements} and defaults to fifty rows; while the screen asked for none of it, it showed
 * the first fifty of 149 suppliers and said nothing — a cut list that does not say it is cut.
 */
/** Small text under a value: what the figure is and what it leaves out. */
const napomena: React.CSSProperties = { fontSize: 11, fontWeight: 400, lineHeight: 1.35, color: "var(--ink-3)", marginTop: 2 };

export default function SupplierFinanceOverviewPage() {
  const { t, i18n } = useTranslation();
  const locale = intlLocale(i18n.resolvedLanguage);
  const [data, setData] = useState<SupplierFinanceOverview | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [ucitava, setUcitava] = useState(false);
  const [page, setPage] = usePage();
  // Redosled i grupa kašnjenja stoje odvojeno od ostalih filtera, isto kao kod kupaca: redosled nije
  // uslov nego poredak, a grupa se bira klikom na grafikon a ne iz padajućeg spiska.
  const [sort, setSort] = useState<string>("DUG_OPADAJUCE");
  const [bucket, setBucket] = useState<Bucket | undefined>(undefined);
  const init = useMemo<FiltersValue>(() => ({ preset: "ytd", ...presetRange("ytd") }), []);
  const [filters, setFilters] = useState<FiltersValue>(init);

  useEffect(() => {
    setUcitava(true);
    supplierFinanceApi.overview(
      {
        from: filters.from, to: filters.to, q: filters.q,
        // Više država ide kao jedan spisak, isto kao kod kupaca.
        country: filters.countries?.join(","), currency: filters.currency,
        bucket, sort,
      },
      page,
    )
      .then(setData)
      .catch((e: ApiError) => setError(e.message))
      .finally(() => setUcitava(false));
  }, [filters.from, filters.to, filters.q, filters.countries?.join(","), filters.currency,
      bucket, sort, page]);

  const onChange = (v: FiltersValue) => {
    const range = v.preset === "custom" ? { from: v.from, to: v.to } : presetRange(v.preset);
    // Svaka promena filtera vraća na prvu stranu: filter koji suzi spisak, dok se stoji na trećoj,
    // inače daje prazan ekran koji izgleda kao da pogodaka nema.
    setPage(0);
    setFilters({ ...v, ...range });
  };

  return (
    <div className="fadein" style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <div className="page-head">
        <div>
          <h1>{t("supplierFinance.title", "Supplier finance")}</h1>
          <div className="sub">{t("supplierFinance.subtitle.overview", "Balances across your suppliers")}</div>
        </div>
        <div className="page-actions">
          {/* Tržište je jedina kontrola koje ovde nema, i to je pravilo: OTKUP nema tržište kao
              opseg. Komponenta ga sama skriva na praznom spisku, pa se ne prosleđuje ništa posebno. */}
          <FinanceFilters
            value={filters}
            markets={[]}
            countries={data?.availableCountries ?? []}
            currencies={data?.availableCurrencies ?? []}
            searchPlaceholder={t("supplierFinance.filter.search", "Dobavljač, PIB ili matični broj…")}
            sort={sort as never}
            onSortChange={(r) => { setSort(r as unknown as string); setPage(0); }}
            onChange={onChange}
          />
        </div>
      </div>

      {error && <div className="empty">{error}</div>}
      {!data ? (
        <TableSkeleton rows={8} cols={6} label={t("common.loading", "Učitavam…")} />
      ) : (
        <>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 12 }}>
            <KPI label={t("supplierFinance.kpi.balance", "Total balance")} value={money(data.totals.balanceEur, filters.currency ?? "EUR")} delta={undefined} trend={undefined} icon={undefined} />
            {/* Counted on the day the incoming invoices reach, which can be earlier than today; the
                tile and the bands below name that day. */}
            <KPI
              label={t("finance.kpi.overdue", "Overdue")}
              value={<>
                {money(data.totals.overdueEur, filters.currency ?? "EUR")}
                {data.asOf && (
                  <div style={napomena}>
                    {t("finance.asOf", "stanje na dan {{datum}}", { datum: fmtDate(data.asOf) })}
                  </div>
                )}
              </>}
              delta={undefined} trend={undefined} icon={undefined}
            />
            {/* Purchases only, the same rule as the customer side: the opening balance and the other
                postings are owed too, but summed as turnover they would set a year that carries an
                opening balance against one that does not, so they are named under the value. */}
            <KPI
              label={t("finance.kpi.turnover", "Turnover")}
              value={<>
                {money(data.totals.turnoverEur, filters.currency ?? "EUR")}
                <div style={napomena}>{t("finance.kpi.turnoverHint", "računi, sa PDV-om")}</div>
                {((data.totals.carriedPostedEur ?? 0) > 0 || (data.totals.otherPostedEur ?? 0) > 0) && (
                  <div style={napomena}>
                    {t("finance.notInvoiced", "Nije uračunato: preneto iz ranijih godina {{prenos}}, ostala knjiženja {{ostalo}}", {
                      prenos: money(data.totals.carriedPostedEur ?? 0, filters.currency ?? "EUR"),
                      ostalo: money(data.totals.otherPostedEur ?? 0, filters.currency ?? "EUR"),
                    })}
                  </div>
                )}
              </>}
              delta={undefined} trend={undefined} icon={undefined}
            />
            <KPI label={t("supplierFinance.kpi.suppliers", "Suppliers")} value={String(data.totals.companyCount)} delta={undefined} trend={undefined} icon={undefined} />
            <KPI label={t("finance.kpi.withOverdue", "With overdue")} value={String(data.totals.companiesWithOverdue)} delta={undefined} trend={undefined} icon={undefined} />
          </div>

          {/* Grupe kašnjenja: isti grafikon koji kupci imaju, nad istim rasponima, i klik na grupu
              sužava spisak. Mereno 06.10.2026, raspored je po smislu različit od kupačkog — kod
              dobavljača najveći iznos stoji u „8–30 dana", kod kupaca u „preko 120".

              SVI IZNOSI NA STRANI NOSE IZABRANU VALUTU, i to od 13.10.2026. Dok su je nosili samo
              grafikon i traka, a KPI i kolone evro, ista strana je o istom broju tvrdila dve
              stvari: za „Imes — u Stečaju" uz izbor RSD grafikon je pisao 4.271,08 RSD, a kolona
              4.271,08 €. Jedan od ta dva je morao biti netačan, i bio je prvi. */}
          <AgingChart
            totals={data.totals}
            selected={bucket}
            onSelect={(b) => { setBucket(b); setPage(0); }}
            currency={filters.currency ?? "EUR"}
            naDan={data.asOf ? t("finance.asOf", "stanje na dan {{datum}}", { datum: fmtDate(data.asOf) }) : undefined}
          />

          {/* Only across currencies: with one currency chosen nothing is converted, so nothing can be
              left out of the totals for want of a rate. */}
          {!filters.currency && data.totals.unconvertedCount > 0 && (
            <div className="empty" style={{ textAlign: "left" }}>
              {t(
                "finance.unconverted",
                "{{count}} amount(s) are kept in a currency with no exchange rate on file and are not included in the totals above.",
                { count: data.totals.unconvertedCount },
              )}
            </div>
          )}

          {data.companies.length === 0 ? (
            <div className="empty">{t("supplierFinance.overview.empty", "No suppliers match the selected filters.")}</div>
          ) : (
            <div style={{
              opacity: ucitava ? 0.55 : 1,
              pointerEvents: ucitava ? "none" : undefined,
              transition: "opacity 120ms",
            }}>
            <Pager page={data.page} size={data.size} total={data.totalElements} busy={ucitava} onPage={setPage} />
            <div className="card" style={{ overflow: "hidden", padding: 0 }}>
            <table className="tbl">
              <thead>
                <tr>
                  <th>{t("supplierFinance.overview.company", "Supplier")}</th>
                  <th>{t("finance.filter.country", "Country")}</th>
                  <th style={{ textAlign: "right" }}>{t("finance.kpi.balance", "Balance")}</th>
                  <th style={{ textAlign: "right" }}>{t("finance.aging.notDue", "Nedospelo")}</th>
                  <th style={{ textAlign: "right" }}>{t("finance.kpi.overdue", "Overdue")}</th>
                  <th style={{ textAlign: "right" }}>{t("finance.kpi.turnover", "Turnover")}</th>
                  <th>{t("finance.overview.lastPayment", "Last payment")}</th>
                </tr>
              </thead>
              <tbody>
                {data.companies.map((c) => (
                  <tr key={c.companyId} className="row-link">
                    <td><Link to={`/procurement/finance/companies/${c.companyId}`} style={{ color: "var(--brand-primary)", textDecoration: "none" }}>{c.name}</Link></td>
                    <td>{c.country ? countryName(c.country, i18n.resolvedLanguage ?? "sr") : "—"}</td>
                    <td style={{ textAlign: "right" }}>
                      {/* Valuta se prosleđuje kao OBAVEZAN izbor, ne kao podrazumevani evro: polje se
                          zove `balanceEur` a nosi sirov dinarski iznos kad je izabran RSD, pa bez
                          ovoga ćelija upiše „EUR" nad dinarima i precijeni iznos 117×. */}
                      <AmountByCurrency
                        eur={c.balanceEur}
                        byCurrency={c.balanceByCurrency}
                        locale={locale}
                        currency={filters.currency ?? "EUR"}
                      />
                    </td>
                    <td style={{ textAlign: "right" }}>{money(c.notDueEur, filters.currency ?? "EUR")}</td>
                    <td style={{ textAlign: "right", color: c.overdueEur > 0 ? "var(--err)" : undefined }}>
                      {money(c.overdueEur, filters.currency ?? "EUR")}
                      <div style={{ display: "flex", justifyContent: "flex-end" }}>
                        <AgingStrip row={c} currency={filters.currency ?? "EUR"} />
                      </div>
                    </td>
                    <td style={{ textAlign: "right" }}>{money(c.turnoverEur, filters.currency ?? "EUR")}</td>
                    <td>{fmtDate(c.lastPaymentDate)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
            <Pager page={data.page} size={data.size} total={data.totalElements} busy={ucitava} onPage={setPage} />
            </div>
          )}
        </>
      )}
    </div>
  );
}
